import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "../context/AppContext";
import { META, pickExam, recordExam, recordAnswer } from "../lib/store";
import {
  GraduationCap,
  Clock,
  Flag,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export default function Exam() {
  const { level } = useApp();
  const cfg = META[level];
  const [phase, setPhase] = useState("intro"); // intro | running | result
  const [qs, setQs] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [left, setLeft] = useState(cfg.durationMin * 60);
  const [result, setResult] = useState(null);
  const timer = useRef(null);

  const start = () => {
    const picked = pickExam(level);
    setQs(picked);
    setAnswers({});
    setIdx(0);
    setLeft(cfg.durationMin * 60);
    setPhase("running");
  };

  const submit = useCallback(() => {
    clearInterval(timer.current);
    let correct = 0;
    const secMap = {};
    qs.forEach((q) => {
      const a = answers[q.id];
      const ok = a === q.answer;
      if (ok) correct += 1;
      recordAnswer(level, q.id, !!ok);
      if (!secMap[q.section]) secMap[q.section] = { name: q.sectionName, total: 0, correct: 0 };
      secMap[q.section].total += 1;
      if (ok) secMap[q.section].correct += 1;
    });
    const percent = Math.round((correct / qs.length) * 100);
    const passed = percent >= cfg.passMark;
    const res = { correct, total: qs.length, percent, passed, sections: secMap };
    recordExam(level, res);
    setResult(res);
    setPhase("result");
  }, [qs, answers, level, cfg.passMark]);

  useEffect(() => {
    if (phase !== "running") return;
    timer.current = setInterval(() => {
      setLeft((l) => {
        if (l <= 1) {
          clearInterval(timer.current);
          submit();
          return 0;
        }
        return l - 1;
      });
    }, 1000);
    return () => clearInterval(timer.current);
  }, [phase, submit]);

  // INTRO
  if (phase === "intro") {
    return (
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-12 h-12 rounded-sm bg-primary/10 text-primary">
            <GraduationCap size={24} />
          </span>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Examen blanc</h1>
            <p className="text-muted-foreground text-sm mt-1">Simulation des conditions réelles ISED · {cfg.label}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { v: cfg.examQuestions, l: "Questions" },
            { v: `${cfg.durationMin} min`, l: "Durée" },
            { v: `${cfg.passMark}%`, l: "Note de passage" },
          ].map((x) => (
            <div key={x.l} className="rounded-sm border border-border bg-card p-4 text-center">
              <div className="font-mono text-2xl font-bold tabular">{x.v}</div>
              <div className="text-xs text-muted-foreground mt-1">{x.l}</div>
            </div>
          ))}
        </div>
        <div className="rounded-sm border border-border bg-card p-5 text-sm text-muted-foreground space-y-2">
          <p>• Les questions sont tirées aléatoirement de la banque officielle, réparties par section.</p>
          <p>• Le minuteur se lance dès le début ; l'examen se soumet automatiquement à la fin du temps.</p>
          <p>• Vous pourrez revoir toutes vos réponses à la fin.</p>
        </div>
        <button onClick={start} data-testid="exam-start" className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-3.5 font-semibold hover:-translate-y-px transition-transform">
          Commencer l'examen <ChevronRight size={18} />
        </button>
      </div>
    );
  }

  // RESULT
  if (phase === "result") {
    return (
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="rounded-sm border border-border bg-card p-8 text-center">
          <div className="font-mono text-7xl font-bold tabular" style={{ color: result.passed ? "hsl(var(--success))" : "hsl(var(--destructive))" }}>
            {result.percent}%
          </div>
          <div className={`inline-flex items-center gap-2 mt-4 px-4 py-1.5 rounded-sm font-semibold ${result.passed ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`} data-testid="exam-verdict">
            {result.passed ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
            {result.passed ? "Réussi" : "Échec"}
          </div>
          <p className="text-muted-foreground text-sm mt-3">
            {result.correct} / {result.total} bonnes réponses · note de passage {cfg.passMark}%
          </p>
        </div>

        <div className="rounded-sm border border-border bg-card p-6">
          <h2 className="font-display font-bold text-lg mb-4">Résultats par section</h2>
          <div className="space-y-3">
            {Object.entries(result.sections).map(([code, s]) => {
              const pct = Math.round((s.correct / s.total) * 100);
              return (
                <div key={code}>
                  <div className="flex justify-between text-sm mb-1">
                    <span><span className="font-mono text-xs text-primary mr-1">{code}</span>{s.name}</span>
                    <span className="font-mono">{s.correct}/{s.total}</span>
                  </div>
                  <div className="h-2 rounded-sm bg-muted overflow-hidden">
                    <div className="h-full transition-[width]" style={{ width: `${pct}%`, background: pct >= cfg.passMark ? "hsl(var(--success))" : "hsl(var(--warning))" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="font-display font-bold text-lg">Révision des réponses</h2>
          {qs.map((q, i) => {
            const a = answers[q.id];
            const ok = a === q.answer;
            return (
              <div key={q.id} className="rounded-sm border border-border bg-card p-4">
                <div className="flex items-start gap-2">
                  {ok ? <CheckCircle2 size={18} className="text-success mt-0.5 shrink-0" /> : <XCircle size={18} className="text-destructive mt-0.5 shrink-0" />}
                  <div className="flex-1">
                    <span className="font-mono text-xs text-muted-foreground">{i + 1}. {q.id}</span>
                    <p className="font-medium text-sm mt-1">{q.question}</p>
                    <p className="text-sm mt-2 text-success">✓ {q.options[q.answer]}</p>
                    {!ok && a !== undefined && (
                      <p className="text-sm text-destructive">✗ Votre réponse : {q.options[a]}</p>
                    )}
                    {a === undefined && <p className="text-sm text-muted-foreground">Sans réponse</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-3">
          <button onClick={() => setPhase("intro")} data-testid="exam-retry" className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-3 font-semibold hover:-translate-y-px transition-transform">
            <RefreshCw size={18} /> Nouvel examen
          </button>
          <Link to="/" className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm border border-border px-4 py-3 font-semibold hover:bg-secondary transition-colors">
            <ArrowLeft size={18} /> Tableau de bord
          </Link>
        </div>
      </div>
    );
  }

  // RUNNING (focus mode)
  const q = qs[idx];
  const answeredCount = Object.keys(answers).length;
  const lowTime = left <= 300;
  return (
    <div className="fixed inset-0 z-50 bg-background grain flex flex-col">
      <div className="border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="font-display font-bold">
            Examen <span className="text-muted-foreground font-normal text-sm">· {cfg.label}</span>
          </div>
          <div className={`flex items-center gap-2 font-mono text-xl font-bold tabular px-3 py-1 rounded-sm ${lowTime ? "text-destructive bg-destructive/10 animate-pulse" : "text-foreground"}`} data-testid="exam-timer">
            <Clock size={18} /> {fmt(left)}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm text-muted-foreground">Question {idx + 1} / {qs.length}</span>
            <span className="font-mono text-sm text-primary">{answeredCount} répondues</span>
          </div>

          <div className="rounded-sm border border-border bg-card p-6">
            <span className="font-mono text-xs text-primary">{q.id}</span>
            <h2 className="font-display font-bold text-xl sm:text-2xl mt-2 leading-snug">{q.question}</h2>
          </div>

          <div className="space-y-2.5">
            {q.options.map((opt, oi) => {
              const sel = answers[q.id] === oi;
              return (
                <button
                  key={opt}
                  onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                  data-testid={`exam-option-${oi}`}
                  className={`w-full text-left flex items-center gap-3 rounded-sm border px-4 py-3.5 text-sm transition-colors ${
                    sel ? "border-primary bg-primary/12" : "border-border hover:border-primary/50 hover:bg-secondary/40"
                  }`}
                >
                  <span className={`font-mono text-xs w-5 h-5 grid place-items-center rounded-sm shrink-0 ${sel ? "bg-primary text-primary-foreground" : "border border-border"}`}>
                    {String.fromCharCode(65 + oi)}
                  </span>
                  <span className="flex-1">{opt}</span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-3">
            <button disabled={idx === 0} onClick={() => setIdx((i) => i - 1)} data-testid="exam-prev" className="inline-flex items-center gap-1 rounded-sm border border-border px-4 py-2.5 text-sm disabled:opacity-40 hover:bg-secondary transition-colors">
              <ChevronLeft size={16} /> Précédent
            </button>
            {idx + 1 < qs.length ? (
              <button onClick={() => setIdx((i) => i + 1)} data-testid="exam-next" className="inline-flex items-center gap-1 rounded-sm bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:-translate-y-px transition-transform">
                Suivant <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={submit} data-testid="exam-submit" className="inline-flex items-center gap-2 rounded-sm bg-success text-white px-5 py-2.5 text-sm font-semibold hover:-translate-y-px transition-transform">
                <Flag size={16} /> Soumettre
              </button>
            )}
          </div>

          {/* question palette */}
          <div className="rounded-sm border border-border bg-card p-4">
            <div className="text-xs text-muted-foreground mb-3">Navigation rapide</div>
            <div className="grid grid-cols-8 sm:grid-cols-12 gap-1.5">
              {qs.map((qq, i) => {
                const done = answers[qq.id] !== undefined;
                return (
                  <button
                    key={qq.id}
                    onClick={() => setIdx(i)}
                    className={`aspect-square rounded-sm text-[11px] font-mono transition-colors ${
                      i === idx ? "bg-primary text-primary-foreground" : done ? "bg-success/25 text-foreground" : "bg-muted text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
          </div>

          <button onClick={submit} data-testid="exam-submit-bottom" className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-success/50 text-success px-4 py-3 font-semibold hover:bg-success/10 transition-colors">
            <Flag size={16} /> Terminer et soumettre ({answeredCount}/{qs.length})
          </button>
        </div>
      </div>
    </div>
  );
}
